/**
 * UpgradeCelebrationModal — Feb 27 2026 · Phase 165.
 *
 * Full-screen takeover after a successful Stripe checkout returns paid.
 * Confetti burst, tier badge, "what's new" bullet highlights, and a single
 * CTA to close. Locale strings vary by tier so Premium and VIP feel
 * distinct. Respects `prefers-reduced-motion` (skips confetti + spring).
 *
 * Kept intentionally small — no analytics events, no side effects beyond
 * closing itself. Analytics + tier refresh already happen in the parent.
 */
import { useEffect } from 'react';
import { Crown, Sparkles, X } from 'lucide-react';
import ConfettiBurst from './ConfettiBurst';
import { playCelebrationChime } from '@/lib/celebrationChime';

const COPY = {
  premium: {
    title: 'Welcome to Premium',
    subtitle: 'You just unlocked deeper filters + advanced discovery.',
    tierLabel: 'PREMIUM',
    tone: '#7C3AED',
    perks: [
      'Filter by lifestyle, background & preferences',
      'See who liked you',
      'Unlimited daily swipes',
      'Save up to 5 filter presets',
    ],
  },
  vip: {
    title: 'Welcome to VIP',
    subtitle: 'You just unlocked our best signals + everything Premium.',
    tierLabel: 'VIP',
    tone: '#B91C1C',
    perks: [
      'Minimum Keep Score & Compatibility filters',
      'New Members + Hide Inactive gates',
      'Verified & Online-Only filters',
      'Unlimited saved searches + priority match support',
    ],
  },
};

export default function UpgradeCelebrationModal({ tier, onClose }) {
  const copy = COPY[tier] || COPY.premium;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (reduce) return;
    // Fire the shared celebration chime — same notes as an "It's a Match"
    // moment so users associate this with their other wins.
    try { playCelebrationChime('match'); } catch { /* audio-context may fail */ }
  }, []);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm px-6"
      data-testid={`upgrade-celebration-${tier}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-celebration-title">
      {/* Confetti behind the card */}
      <ConfettiBurst active={true} count={54} testId="upgrade-confetti" />

      {/* Card */}
      <div className="relative w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden animate-scale-in">
        {/* Close X */}
        <button
          onClick={onClose}
          aria-label="Close celebration"
          data-testid="upgrade-celebration-close"
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/[0.04] hover:bg-black/[0.08] flex items-center justify-center transition-colors z-10">
          <X size={16} className="text-zinc-500" />
        </button>

        {/* Header stripe */}
        <div className="pt-12 pb-6 px-8 text-center" style={{ background: `linear-gradient(180deg, ${copy.tone}0F 0%, transparent 100%)` }}>
          <div
            className="inline-flex items-center gap-1.5 mb-4 px-3 py-1 rounded-full text-[10.5px] uppercase tracking-[0.18em] font-[700] text-white"
            style={{ background: copy.tone }}
            data-testid="upgrade-tier-badge">
            {tier === 'vip' ? <Crown size={11} /> : <Sparkles size={11} />}
            {copy.tierLabel}
          </div>
          <h2 id="upgrade-celebration-title" className="font-brand text-[28px] font-[700] tracking-tight text-[#0F0F10] mb-2 leading-tight">
            {copy.title}
          </h2>
          <p className="text-[13px] text-[#8A8A93] leading-relaxed max-w-[26ch] mx-auto">
            {copy.subtitle}
          </p>
        </div>

        {/* Perks */}
        <ul className="px-8 pb-4 space-y-2.5" data-testid="upgrade-perks-list">
          {copy.perks.map((perk, i) => (
            <li key={i} className="flex items-start gap-2.5 text-[13.5px] text-[#0F0F10] leading-snug" style={{ animation: `fade-up 0.5s ease-out ${0.15 + i * 0.08}s both` }}>
              <span
                className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ background: copy.tone }}
              />
              {perk}
            </li>
          ))}
        </ul>

        {/* CTA */}
        <div className="px-8 pb-8 pt-4">
          <button
            onClick={onClose}
            data-testid="upgrade-celebration-cta"
            className="w-full py-3.5 rounded-full text-white font-semibold text-[14px] tracking-tight transition-transform hover:scale-[1.02] active:scale-[0.98]"
            style={{ background: copy.tone }}>
            Start exploring
          </button>
        </div>
      </div>
    </div>
  );
}
